<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Бухгалтерский отчет</div>

            <div class="content-header__background content-header__background--2-lines-filters"></div>
        </div>

        <FilterPanel :columns="4">
            <UISelect
                id="year"
                v-model="form.date_year"
                label="Год*"
                :options="yearOptions"
                :error-message="errors?.date_year?.join() ?? ''"
            />

            <UISelect
                id="month"
                v-model="form.date_month"
                label="Месяц*"
                :options="monthOptions"
                :error-message="errors?.date_month?.join() ?? ''"
            />

            <UISelect
                id="brand_id"
                v-model="form.brand_id"
                :options="brandOptions"
                label="Марка"
                :error-message="errors?.brand_id?.join() ?? ''"
            />

            <UISelect
                id="models_ids"
                v-model="form.models_ids"
                :options="modelOptions"
                label="Модель"
                multiple
                searchable
                :error-message="errors?.models_ids?.join() ?? ''"
            />

            <UISelect
                id="dealers_ids"
                v-model="form.dealers_ids"
                :options="dealerOptions"
                label="Дилер"
                multiple
                searchable
                :error-message="errors?.dealers_ids?.join() ?? ''"
            />

            <UIInput
                id="vin"
                v-model="form.vin"
                type="text"
                label="VIN"
                :error-message="errors?.vin?.join() ?? ''"
            />

            <UIRange
                v-model="formSellingPrice"
                :min="0"
                :max="CarMaxPrice"
                :interval="CarRangePriceStep"
                unit="₽"
                :format-numbers="true"
                label="Цена"
                :error-message="errors?.selling_price?.join() ?? ''"
            />

            <template #actions-right>
                <div class="filter-panel__action-right">
                    <UISimpleButton
                        button-type="clear"
                        class="filter-panel__action"
                        @clicked="onReset"
                    >
                        Сбросить фильтр
                    </UISimpleButton>
                </div>

                <div class="filter-panel__action-right">
                    <UISimpleButton
                        class="filter-panel__action"
                        :loading="downloadLoading"
                        @clicked="onDownload"
                    >
                        <template #icon>
                            <img v-svg-inline src="@/assets/icons/report.svg" />
                        </template>
                        Сформировать отчёт
                    </UISimpleButton>
                </div>
            </template>
        </FilterPanel>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref } from 'vue';

    import { SelectOption } from '@/types/form.type.ts';
    import { CarMaxPrice, CarRangePriceStep } from '@/types/car.type.ts';
    import { DealerListItem } from '@/types/dealer.type.ts';
    import { Model } from '@/types/model.type.ts';
    import {
        AccountingReportDownloadErrors,
        AccountingReportRequestFilter,
    } from '@/types/report.type.ts';

    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';

    import { getBrandSelectOptions, getReportMonthOptions, getReportYearOptions } from '@/utils/SelectOptionFillers.ts';

    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';

    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';
    import UIRange from '@/components/ui/UIRange/UIRange.vue';

    const { downloadAccountingReport } = useDocumentDownloader();
    const { getAllActiveModels } = useModelRepository();
    const { getAllActiveDealers } = useDealerRepository();

    const form = ref<AccountingReportRequestFilter>({
        date_year: undefined,
        date_month: undefined,
        brand_id: undefined,
        models_ids: [],
        dealers_ids: [],
        vin: '',
        selling_price: undefined,
    });

    const yearOptions = ref<SelectOption[]>(getReportYearOptions());
    const brandOptions = ref<SelectOption[]>(getBrandSelectOptions());
    const dealerOptions = ref<SelectOption[]>([]);

    const errors = ref<AccountingReportDownloadErrors | null>(null);
    const downloadLoading = ref<boolean>(false);

    const arModels = ref<Model[]>([]);

    const formSellingPrice = computed<number[]>({
        get: () => !!form.value.selling_price ? form.value.selling_price : [0, CarMaxPrice],
        set: (prices) => {
            form.value.selling_price = [prices[0], prices[1]];
        },
    });

    const monthOptions = computed<SelectOption[]>(() => {
        if (form.value.date_year === undefined) {
            return [];
        }

        return getReportMonthOptions(form.value.date_year);
    });

    const modelOptions = computed<SelectOption[]>(() => {
        return arModels.value
            .filter(
                (model: Model) => {
                    return !form.value.brand_id || form.value.brand_id === model.brand_id;
                },
            )
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));
    });

    function onDownload(): void {
        downloadLoading.value = true;
        errors.value = null;

        downloadAccountingReport('accounting-report.xlsx', mapFormToRequest())
            .catch((downloadErrors: AccountingReportDownloadErrors) => {
                errors.value = downloadErrors;
            })
            .finally(() => {
                downloadLoading.value = false;
            });
    }

    function onReset(): void {
        form.value = {
            date_year: undefined,
            date_month: undefined,
            brand_id: undefined,
            models_ids: [],
            dealers_ids: [],
            vin: '',
            selling_price: undefined,
        };
    }

    function mapFormToRequest(): AccountingReportRequestFilter {
        const request: AccountingReportRequestFilter = {};

        if (form.value.date_year !== undefined) {
            request.date_year = form.value.date_year;
        }

        if (form.value.date_month !== undefined) {
            request.date_month = form.value.date_month;
        }

        if (form.value.brand_id !== undefined) {
            request.brand_id = form.value.brand_id;
        }

        if (form.value.models_ids !== undefined && form.value.models_ids.length > 0) {
            request.models_ids = form.value.models_ids;
        }

        if (form.value.dealers_ids !== undefined && form.value.dealers_ids.length > 0) {
            request.dealers_ids = form.value.dealers_ids;
        }

        if (form.value.vin !== undefined && form.value.vin.length > 0) {
            request.vin = form.value.vin;
        }

        if (form.value.selling_price !== undefined && !(form.value.selling_price[0] === 0 && form.value.selling_price[1] === CarMaxPrice)) {
            request.selling_price = form.value.selling_price;
        }

        return request;
    }

    function fetchFilterOptions(): void {
        getAllActiveModels().then((models: Model[]) => {
            arModels.value = models;
        });

        getAllActiveDealers().then((dealers: DealerListItem[]) => {
            dealerOptions.value = dealers.map((dealer: DealerListItem) => ({
                label: `${dealer.name} (${dealer.sap_code})`,
                value: dealer.id,
            }));
        });
    }

    onMounted(() => {
        fetchFilterOptions();
    });
</script>
